import { useEffect, useState, type ReactNode } from "react";
import { ArrowRight, ShieldCheck, Users, MessageSquareText } from "lucide-react";
import { Link } from "react-router-dom";
import { Wordmark } from "../components/Chrome";
import { PublicFooter } from "../components/PublicLayout";
import { PUBLIC_BRAND, publicBrandStatus } from "../api/brand";

const HERO_IMG =
  "https://images.unsplash.com/photo-1679391029864-d46f366a456b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000";

const HOME = `${PUBLIC_BRAND}/homepage_background`;

function HomeBackdrop() {
  const [exists, setExists] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { let on = true; publicBrandStatus().then((s) => on && setExists(Boolean(s.homepage_background))); return () => { on = false; }; }, []);
  if (!exists) return null;
  return (
    <img src={`${HOME}?w=1920`} srcSet={`${HOME}?w=960 960w, ${HOME}?w=1920 1920w`} sizes="100vw" alt="" aria-hidden onLoad={() => setLoaded(true)}
      data-testid="landing-backdrop-live"
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 motion-reduce:transition-none ${loaded ? "opacity-100" : "opacity-0"}`} />
  );
}

function EntryCard({ to, testId, icon, title, body }: { to: string; testId: string; icon: ReactNode; title: string; body: string }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="glass-raised group flex flex-col justify-between gap-10 rounded-[22px] p-8 transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-signal/60 hover:shadow-[0_0_0_1px_rgba(255,29,0,0.35),0_0_32px_-8px_rgba(255,29,0,0.55)] active:scale-[0.98]"
    >
      <div className="flex items-center justify-between text-zinc-400">
        {icon}
        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-signal" />
      </div>
      <div>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white">{title}</h2>
        <p className="mt-2 text-sm text-zinc-400">{body}</p>
      </div>
    </Link>
  );
}

export default function Landing() {
  return (
    <div data-testid="landing-page" className="relative min-h-[calc(100vh-var(--banner-h))] overflow-hidden">
      <img src={HERO_IMG} alt="Stadium lights at night" className="absolute inset-0 h-full w-full object-cover" />
      <HomeBackdrop />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink" />
      <div className="absolute inset-0 grain" />
      <div className="relative mx-auto flex min-h-[calc(100vh-var(--banner-h))] max-w-7xl flex-col items-center justify-center px-6 py-12 text-center sm:px-12">
        <header className="flex w-full justify-center">
          <Wordmark size="hero" />
          <h1 className="sr-only">Gameday Time For The Crew</h1>
        </header>
        <div className="flex w-full flex-col items-center pt-8 sm:pt-10">
          <p className="mx-auto max-w-2xl animate-rise text-center font-display text-xl font-light leading-snug text-zinc-200 [animation-delay:160ms] sm:text-2xl lg:text-3xl" data-testid="landing-description">
            <span className="block">You’re on the Roster Now!</span>{" "}
            <span className="block">Use Your Phone to Clock In for Gameday</span>
          </p>
          <div className="mx-auto mt-10 grid w-full max-w-4xl animate-rise gap-4 text-left sm:mt-12 [animation-delay:240ms] md:grid-cols-2">
            <EntryCard
              to="/login"
              testId="landing-employee-login-link"
              icon={<Users className="h-6 w-6" />}
              title="Welcome Crew"
              body="Crews: Get a One Time code by Email to Verify!"
            />
            <EntryCard
              to="/admin/login"
              testId="landing-admin-login-link"
              icon={<ShieldCheck className="h-6 w-6" />}
              title="Manage Time"
              body="Crew Leads: Need Password + Authenticator App"
            />
          </div>
          <Link to="/sms" data-testid="landing-sms-program-link" className="group mx-auto mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-5 py-3 text-sm font-medium text-white transition-[border-color,background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-signal/60 hover:bg-white/[0.09] hover:shadow-[0_0_0_1px_rgba(255,29,0,0.35),0_0_24px_-6px_rgba(255,29,0,0.55)] focus-visible:-translate-y-0.5 focus-visible:border-signal/60 focus-visible:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-ink active:scale-[0.98] motion-reduce:transform-none motion-reduce:hover:translate-y-0 motion-reduce:focus-visible:translate-y-0 motion-reduce:active:scale-100">
            <MessageSquareText className="h-4 w-4 text-signal transition-[transform,filter] duration-200 group-hover:translate-x-0.5 group-hover:brightness-125 group-hover:drop-shadow-[0_0_6px_rgba(255,29,0,0.7)] group-focus-visible:brightness-125 motion-reduce:group-hover:translate-x-0" />SMS Guide
          </Link>
        </div>
      </div>
      <div className="relative"><PublicFooter centered /></div>
    </div>
  );
}
